import { Core } from "@faire/design-tokens";
import { useGetFaireAcademyPageState } from "@faire/retailer-visitor-shared/hooks/openWithFaire/useGetFaireAcademyPageState";
import { CHECK_ANIMATION_MS } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/components/animationTimingConsts";
import { useGetActiveChapterHash } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/useGetActiveChapterHash";
import type { FaireAcademyChapter } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/types";
import { Book } from "@faire/web--source/slate/icons/foundation/Book";
import { CheckInCircle } from "@faire/web--source/slate/icons/foundation/CheckInCircle";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { trackOpenWithFaireIndividualGuideChapterCtaClick } from "@faire/web-api--source/events/openWithFaire/click/individualGuideChapterCta";
import * as React from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { css } from "styled-components";

export const ChapterNavCard: React.FC<{
  guideTitle: string;
  chapter: FaireAcademyChapter;
  isRead: boolean;
}> = ({ guideTitle, chapter, isRead }) => {
  const activeChapterHash = useGetActiveChapterHash();
  const { title, titleHash, readTimeMinutes } = chapter;
  const isActive: boolean = activeChapterHash === titleHash;
  const textColor = isActive ? Core.text.default : Core.text.subdued;

  return (
    <CardLinkWrapper
      isActive={isActive}
      guideTitle={guideTitle}
      titleHash={titleHash}
    >
      <ChapterCardContainer justify="space-between" $isActive={isActive}>
        <Column gap="1x">
          <Typography variant="paragraphSansMedium" color={textColor}>
            {title}
          </Typography>
          <Row gap="1x" align="center">
            <Book />
            <Typography color={textColor}>
              <StrictLocalMsg
                defaultMessage="{readTimeMinutesNumber, plural, one {# min} other {# mins}}"
                description={{
                  text: "Indicating the estimated time it takes to read an educational guide",
                  img: "https://cdn.faire.com/fastly/35ddd722e22a8b434fe8bb01ff7242afdbe6e6d3a73719af7c2e1d54d098a475.png",
                }}
                values={{ readTimeMinutesNumber: readTimeMinutes }}
              />
            </Typography>
          </Row>
        </Column>
        <FadeInCheckInCircle
          color={Core.border.success}
          data-test-id="chapter-read-icon"
          $isRead={isRead}
        />
      </ChapterCardContainer>
    </CardLinkWrapper>
  );
};

const CardLinkWrapper: React.FC<{
  isActive: boolean;
  guideTitle: string;
  titleHash: string;
  children: React.ReactNode;
}> = ({ isActive, guideTitle, titleHash, children }) => {
  const { pageState } = useGetFaireAcademyPageState();
  const location = useLocation();

  if (isActive) {
    return <>{children}</>;
  }

  return (
    <Link
      to={{ ...location, hash: titleHash }}
      onClick={() =>
        trackOpenWithFaireIndividualGuideChapterCtaClick(
          pageState,
          guideTitle,
          titleHash
        )
      }
    >
      {children}
    </Link>
  );
};

const ChapterCardContainer = styled(Row)<{ $isActive: boolean }>`
  padding: ${getSpacing("2x")};
  border: 1px solid
    ${({ $isActive }) => ($isActive ? Core.border.default : Core.border.muted)};
  border-radius: 8px;
  width: 284px;

  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;

const FadeInCheckInCircle = styled(CheckInCircle)<{ $isRead: boolean }>`
  transition: opacity ${CHECK_ANIMATION_MS}ms;
  ${({ $isRead }) =>
    $isRead
      ? css`
          opacity: 1;
          visibility: visible;
        `
      : css`
          opacity: 0;
          visibility: hidden;
        `}
`;
